import 'maplibre-gl/dist/maplibre-gl.css';

import { setWorkerUrl } from 'maplibre-gl';
import type { ExpressionSpecification, Map as MapLibreMap } from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Layer, Map as MapGl, Source } from 'react-map-gl/maplibre';
import type { MapLayerMouseEvent, MapRef } from 'react-map-gl/maplibre';
import { twMerge } from 'tailwind-merge';

import { formatDateTime } from '@repo/utils/format-date';
import { useNumberFormatter } from '@repo/utils/use-number-formatter';

// A vessel fix, as the positions API and the overview dashboard give it.
export type MapPosition = {
  transporterId: string;
  positionTimestamp: string;
  latitude: number;
  longitude: number;
  speedOverGround?: number | null | undefined;
  vesselCourseDegrees?: number | null | undefined;
  headingDegrees?: number | null | undefined;
};
// A terminal on the map: a dot and its name.
export type MapTerminal = { id: string; name: string; latitude: number; longitude: number };
// What a vessel does, for the hover card of its marker and the line to its next stop.
export type MapVoyage = {
  transportId: string;
  label: string;
  status: string;
  onBoardTonnes: number | null;
  // Not reached, so its arrival is an estimate.
  nextStop: { terminalId: string | null; name: string; arrival: string } | null;
};

// MapLibre 6 resolves its render worker as a file sibling to the main module,
// which does not survive Vite's dep pre-bundling (dev) or chunking (build) —
// the worker 404s and the map paints only its background colour, no tiles and
// no layers. Point it at a Vite-bundled worker URL instead.
setWorkerUrl(maplibreWorkerUrl);

// KiMS onboard-GPS fixes on a MapLibre map: one marker per vessel at its
// current position (rotated by heading) plus an hourly-history trail over the
// page's selected period. Hourly fixes make angular polylines — expected.
//
// Basemap: OpenFreeMap (commercial use allowed; ODbL attribution rendered by
// the map's attribution control). The style is keyed here so the planned
// migration to a self-hosted Protomaps .pmtiles is a URL swap and nothing else.
const BASEMAP_STYLE_URL = {
  light: 'https://tiles.openfreemap.org/styles/positron',
  dark: 'https://tiles.openfreemap.org/styles/dark',
} as const;

export type ThemeMode = 'light' | 'dark';

// The frame the map never crops below: the southern Norwegian coast from
// Stavanger round to the Oslofjord, plus the sea lane west of it. Vessels
// cluster in the North Sea, so fitting their bounding box alone pushed the
// coast they are sailing to off the edge and left the picture unreadable.
// Unioned into the fitted bounds rather than replacing them, so a vessel
// outside this box still pulls the frame out to include itself.
const MIN_BOUNDS = { minLng: 3, minLat: 57.5, maxLng: 11.5, maxLat: 60.5 };

const INITIAL_VIEW_STATE = {
  longitude: (MIN_BOUNDS.minLng + MIN_BOUNDS.maxLng) / 2,
  latitude: (MIN_BOUNDS.minLat + MIN_BOUNDS.maxLat) / 2,
  zoom: 4,
} as const;

// Trail styling follows the design doc "Vessel Trail Styles": dark mode is
// the "Heating Up" heat ramp (variant 3B), deep violet at the oldest fix
// climbing to near-white at the vessel with opacity baked into the stops;
// light mode is its "Solid Ink" counterpart, the ramp reversed in value,
// palest at the oldest fix and deepest ink at the vessel, with no opacity
// fade (on light ground, value carries recency better than transparency).
// Recency reads as heat/ink, so no arrowheads or chevrons. Adapted from the
// spec: the ramp runs over each vessel's whole visible trail (not the spec's
// fixed eight segments), and sizes are scaled to our 28px marker (the spec's
// reference marker is 46px).
//
// A dash is a time, not a length: each one is a line of its own, and the ramp
// reads its place in the trail (`recency`, 0 at the oldest fix and 1 at the
// vessel).
//
// Every vessel shares the ramp; identity comes from the marker labels.
const TRAIL_GRADIENT: Record<ThemeMode, ExpressionSpecification> = {
  light: [
    'interpolate',
    ['linear'],
    ['get', 'recency'],
    0,
    '#DDD3FF',
    0.14,
    '#CDBDFE',
    0.29,
    '#B8A2FD',
    0.43,
    '#A284FB',
    0.57,
    '#8B5CF6',
    0.71,
    '#7530E8',
    0.86,
    '#6021C9',
    1,
    '#3F1583',
  ],
  dark: [
    'interpolate',
    ['linear'],
    ['get', 'recency'],
    0,
    'rgba(91, 33, 182, 0.55)',
    0.15,
    'rgba(109, 40, 217, 0.7)',
    0.3,
    'rgba(124, 58, 237, 0.8)',
    0.45,
    'rgba(139, 92, 246, 0.88)',
    0.6,
    '#9D78F8',
    0.75,
    '#AF93FA',
    0.9,
    '#C9B4FD',
    1,
    '#F3ECFF',
  ],
};

// Dark mode only: the "Heating Up" per-dash glow (4px growing to 22px),
// approximated as a wider blurred line under the dashes whose gradient ramps
// towards the newest fix. "Solid Ink" has no trail glow.
const TRAIL_GLOW_GRADIENT: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['get', 'recency'],
  0,
  'rgba(91, 33, 182, 0.18)',
  0.6,
  'rgba(139, 92, 246, 0.35)',
  1,
  'rgba(243, 236, 255, 0.85)',
];

// The trail shows the speed: a dash is the way the vessel went in a fixed
// time, so a long dash is a fast vessel and a vessel at rest draws none.
export const TRAIL_DASH_MINUTES = 30;
const TRAIL_DASH_MS = TRAIL_DASH_MINUTES * 60_000;
// The part of that time that is drawn. The rest is the gap to the next dash.
const TRAIL_DASH_DRAWN = 0.6;

// Vessel marker styling: lucide's mouse-pointer-2 glyph and the vessel name
// below it, per the design doc "Neon Vessel Markers".
//
// Dark mode is "Tight / Hot" (variant 2A): a violet tube stroke under a
// near-white core with one tight bloom, and a glowing uppercase label.
//
// Light mode is its daylight counterpart "Solid Ink": a solid violet arrow
// with a white keyline over a soft radial aura. Departure from the spec by
// request: the label is not a chip but violet text with a white halo.
//
// One spec item is approximated in both themes because MapLibre symbol
// labels cannot express it: the Barlow Condensed type (glyphs are limited to
// what the tile host serves). Dark mode also approximates the three-layer
// text glow as a single halo in the spec's mid-glow violet.
const DARK_MARKER = {
  tubeBody: '#7C3AED',
  tubeCore: '#FBF8FF',
  bloom: 'rgba(139, 92, 246, 0.9)',
  labelFill: '#F4EEFF',
  labelGlow: '#8B5CF6',
} as const;

const LIGHT_MARKER = {
  arrowFill: '#6021C9',
  keyline: '#FFFFFF',
  shadow: 'rgba(76, 29, 149, 0.35)',
  aura: 'rgba(124, 58, 237, 0.2)',
  labelHalo: '#FFFFFF',
} as const;

// The terminals and the line to the next stop are context: neutral, so the violet of the vessels
// keeps the eye. The ring is the primary green of a selected row.
const CONTEXT_COLOR: Record<ThemeMode, { ink: string; halo: string }> = {
  light: { ink: '#475569', halo: '#FFFFFF' },
  dark: { ink: '#CBD5E1', halo: '#0F172A' },
};
const HIGHLIGHT_COLOR = '#10B981';

// Path data of lucide-react's mouse-pointer-2 icon (24x24 viewBox). MapLibre
// symbol layers take raster images, not React components, so the glyph is
// rasterised to ImageData here instead of rendered via lucide-react.
const MOUSE_POINTER_2_PATH = 'M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z';
const ICON_VIEWBOX = 24;
const ICON_RATIO = 2;

function makeIconContext(logicalSize: number, pad: number): { ctx: CanvasRenderingContext2D; size: number } | null {
  const size = (logicalSize + pad * 2) * ICON_RATIO;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx)
    return null;
  ctx.scale(ICON_RATIO, ICON_RATIO);
  ctx.translate(pad, pad);
  ctx.scale(logicalSize / ICON_VIEWBOX, logicalSize / ICON_VIEWBOX);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  return { ctx, size };
}

// The spec's marker height H. Both themes derive their sizes from it.
const MARKER_SIZE = 28;
// Stroke widths etc. are in marker pixels; the drawing context is scaled to
// the 24-unit path space, so they are divided back down.
const TO_PATH_UNITS = ICON_VIEWBOX / MARKER_SIZE;

function makeCursorImage(mode: ThemeMode): ImageData {
  return mode === 'dark' ? makeDarkCursorImage() : makeLightCursorImage();
}

// "Tight / Hot": a violet tube body carrying the single close-range bloom,
// then a near-white core on top, scaled from the spec's 46px reference down
// to MARKER_SIZE. Static and radial, so heading rotation does not change
// its weight.
function makeDarkCursorImage(): ImageData {
  // Padding so the bloom is not clipped at the image edge.
  const pad = 7;
  const made = makeIconContext(MARKER_SIZE, pad);
  if (!made)
    return new ImageData((MARKER_SIZE + pad * 2) * ICON_RATIO, (MARKER_SIZE + pad * 2) * ICON_RATIO);
  const { ctx, size } = made;
  const path = new Path2D(MOUSE_POINTER_2_PATH);
  ctx.strokeStyle = DARK_MARKER.tubeBody;
  ctx.lineWidth = 4 * TO_PATH_UNITS;
  ctx.shadowColor = DARK_MARKER.bloom;
  ctx.shadowBlur = 5 * ICON_RATIO;
  ctx.stroke(path);
  ctx.shadowBlur = 0;
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = DARK_MARKER.tubeCore;
  ctx.lineWidth = 1.5 * TO_PATH_UNITS;
  ctx.stroke(path);
  return ctx.getImageData(0, 0, size, size);
}

// "Solid Ink": a radial aura (1.5 H across, 20% fading to nothing) under a
// solid violet arrow with a small drop shadow and a white keyline (0.05 H).
function makeLightCursorImage(): ImageData {
  // Padding fits the aura (0.25 H beyond the marker on each side) plus the
  // drop shadow.
  const pad = 8;
  const made = makeIconContext(MARKER_SIZE, pad);
  if (!made)
    return new ImageData((MARKER_SIZE + pad * 2) * ICON_RATIO, (MARKER_SIZE + pad * 2) * ICON_RATIO);
  const { ctx, size } = made;
  const path = new Path2D(MOUSE_POINTER_2_PATH);
  const center = ICON_VIEWBOX / 2;
  const auraRadius = (MARKER_SIZE * 1.5 / 2) * TO_PATH_UNITS;
  const aura = ctx.createRadialGradient(center, center, 0, center, center, auraRadius);
  aura.addColorStop(0, LIGHT_MARKER.aura);
  aura.addColorStop(1, 'rgba(124, 58, 237, 0)');
  ctx.fillStyle = aura;
  ctx.fillRect(center - auraRadius, center - auraRadius, auraRadius * 2, auraRadius * 2);
  // Shadow offset/blur are in device pixels, untouched by the transform,
  // hence the ratio factor.
  ctx.shadowColor = LIGHT_MARKER.shadow;
  ctx.shadowOffsetY = 1 * ICON_RATIO;
  ctx.shadowBlur = 2 * ICON_RATIO;
  ctx.fillStyle = LIGHT_MARKER.arrowFill;
  ctx.fill(path);
  ctx.shadowColor = 'transparent';
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;
  ctx.strokeStyle = LIGHT_MARKER.keyline;
  ctx.lineWidth = MARKER_SIZE * 0.05 * TO_PATH_UNITS;
  ctx.stroke(path);
  return ctx.getImageData(0, 0, size, size);
}

// Icon images have to be in the style *before* the symbol layer is laid out.
// MapLibre lays a symbol bucket out once and does not revisit it, so an image
// that arrives afterwards is ignored until something forces a fresh layout —
// which is why supplying it late (from `styleimagemissing`, or from a resolver
// registered after the layer mounted) made the markers appear only sometimes,
// on the next position refetch. Both cursors are therefore added up front, and
// the marker layer is held back until they are in.
//
// They have to be re-added after every style swap: pointing the map at the
// other theme's basemap loads a whole new style, which drops runtime images.
function ensureCursorImages(map: MapLibreMap) {
  for (const mode of ['light', 'dark'] as const) {
    const id = `vessel-cursor-${mode}`;
    if (!map.hasImage(id))
      map.addImage(id, makeCursorImage(mode), { pixelRatio: ICON_RATIO });
  }
}

type Props = {
  theme: ThemeMode;
  // The newest fix per vessel, and the history the trails are drawn from.
  current: MapPosition[];
  history: MapPosition[];
  vesselNameById: globalThis.Map<string, string>;
  // No header when absent, for a page that titles the map itself.
  title?: ReactNode;
  // Small text at the right of the title.
  note?: ReactNode;
  isLoading?: boolean | undefined;
  emptyText?: string | undefined;
  // Makes the markers clickable. Called with the vessel's transporter id.
  onVesselSelect?: ((vesselId: string) => void) | undefined;
  // The terminals the voyages call at. Each one is a dot and its name.
  terminals?: MapTerminal[] | undefined;
  // What each vessel does: shown in its hover card, with a line to its next stop.
  voyageByVessel?: globalThis.Map<string, MapVoyage> | undefined;
  // A vessel that another part of the page points at. Its marker gets a ring.
  highlightedVesselId?: string | null | undefined;
  // The same for a terminal.
  highlightedTerminalId?: string | null | undefined;
  // Called with the vessel under the pointer, and with null when there is none.
  onVesselHover?: ((vesselId: string | null) => void) | undefined;
  // How the hover card writes a time. UTC, unless the page shows local time.
  formatTime?: ((time: string) => string) | undefined;
  className?: string | undefined;
};

export function VesselMap({ theme, current, history, vesselNameById, title, note, isLoading = false, emptyText = 'No position data for the selected vessels yet.', onVesselSelect, terminals, voyageByVessel, highlightedVesselId, highlightedTerminalId, onVesselHover, formatTime = formatDateTime, className }: Props) {
  const mapRef = useRef<MapRef>(null);
  // The vessel set the viewport was last fitted to, so a changed fleet refits
  // and an unchanged one is left where the user put it.
  const fittedKeyRef = useRef<string | null>(null);
  const [hover, setHover] = useState<MapPosition | null>(null);

  // Marker per vessel = its newest fix. `current` can be empty (per-environment
  // ingest lag) while `history` already has rows, so fall back to the latest
  // history fix. History is ordered (transporterId, positionTimestamp) asc —
  // the last row per vessel wins; a current fix overrides it.
  const markers = useMemo(() => {
    const byVessel = new Map<string, MapPosition>();
    for (const p of history)
      byVessel.set(p.transporterId, p);
    for (const p of current)
      byVessel.set(p.transporterId, p);
    return [...byVessel.values()];
  }, [history, current]);

  // The dashes of each vessel's trail: its hourly fixes in order, with the
  // current fix appended as the trail head (the backend deliberately does not
  // merge it). The dashes start again at each fix, so two fixes an hour apart
  // have the same number of dashes between them at every speed.
  const trailsGeoJson = useMemo(() => {
    const byVessel = new Map<string, MapPosition[]>();
    for (const p of history) {
      const list = byVessel.get(p.transporterId) ?? [];
      list.push(p);
      byVessel.set(p.transporterId, list);
    }
    for (const p of current) {
      const list = byVessel.get(p.transporterId);
      if (list)
        list.push(p);
    }
    return {
      type: 'FeatureCollection' as const,
      features: [...byVessel.values()].flatMap((fixes) => {
        const times = fixes.map(p => Date.parse(p.positionTimestamp));
        const [first, last] = [times[0] ?? 0, times.at(-1) ?? 0];
        return fixes.slice(1).flatMap((to, i) => {
          const from = fixes[i]!;
          const [start, end] = [times[i]!, times[i + 1]!];
          // The position at a time between the two fixes, on a straight line
          const at = (time: number) => {
            const part = (time - start) / (end - start);
            return [from.longitude + (to.longitude - from.longitude) * part, from.latitude + (to.latitude - from.latitude) * part];
          };
          const dashes = [];
          for (let time = start; time < end; time += TRAIL_DASH_MS) {
            dashes.push({
              type: 'Feature' as const,
              properties: { recency: (time - first) / (last - first) },
              geometry: { type: 'LineString' as const, coordinates: [at(time), at(Math.min(time + TRAIL_DASH_MS * TRAIL_DASH_DRAWN, end))] },
            });
          }
          return dashes;
        });
      }),
    };
  }, [history, current]);

  const markersGeoJson = useMemo(() => {
    // Vessels moored at the same berth land on the same pixel. Group markers
    // by a ~1 km coordinate bucket and give each vessel in a bucket its own
    // label row, so co-located names stack below the icon instead of
    // overprinting each other. Offsets are in ems of the label size.
    const rowsByBucket = new Map<string, number>();
    return {
      type: 'FeatureCollection' as const,
      features: markers.map((p, i) => {
        const bucket = `${p.longitude.toFixed(2)},${p.latitude.toFixed(2)}`;
        const row = rowsByBucket.get(bucket) ?? 0;
        rowsByBucket.set(bucket, row + 1);
        return {
          type: 'Feature' as const,
          // Index back into `markers` on hover, avoids serialising the whole
          // fix into feature properties.
          id: i,
          properties: {
            index: i,
            vesselId: p.transporterId,
            name: vesselNameById.get(p.transporterId) ?? p.transporterId,
            labelOffset: [0, 1.4 + row * 1.2],
            rotation: p.headingDegrees ?? p.vesselCourseDegrees ?? 0,
          },
          geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] },
        };
      }),
    };
  }, [markers, vesselNameById]);

  const terminalsGeoJson = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: (terminals ?? []).map(t => ({
      type: 'Feature' as const,
      properties: { id: t.id, name: t.name },
      geometry: { type: 'Point' as const, coordinates: [t.longitude, t.latitude] },
    })),
  }), [terminals]);

  // From each vessel to the terminal of its next stop. A straight line: it shows where the vessel
  // goes, not the route it sails.
  const nextStopsGeoJson = useMemo(() => {
    const terminalById = new Map((terminals ?? []).map(t => [t.id, t]));
    return {
      type: 'FeatureCollection' as const,
      features: markers.flatMap((p) => {
        const terminal = terminalById.get(voyageByVessel?.get(p.transporterId)?.nextStop?.terminalId ?? '');
        return terminal
          ? [{
              type: 'Feature' as const,
              properties: {},
              geometry: { type: 'LineString' as const, coordinates: [[p.longitude, p.latitude], [terminal.longitude, terminal.latitude]] },
            }]
          : [];
      }),
    };
  }, [markers, terminals, voyageByVessel]);

  // The marker layer must not mount until its images are in the current
  // style. Keyed by theme rather than a plain flag, so a basemap swap takes
  // the layer down until the new style has been given the icons again.
  const [readyTheme, setReadyTheme] = useState<ThemeMode | null>(null);
  // Separate from readyTheme: this one only ever goes true, and gates the
  // viewport fit below on the map existing and having been sized.
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  function handleStyleReady() {
    const map = mapRef.current?.getMap();
    if (!map)
      return;
    ensureCursorImages(map);
    setReadyTheme(theme);
    setIsMapLoaded(true);
  }

  const allFixes = useMemo(() => [...history, ...current], [history, current]);

  // Fit to where the vessels are now, not to every fix in the window: a month
  // of trail covers far more sea than the fleet occupies, and fitting that
  // left the map zoomed out over the whole North Sea.
  //
  // Gated on the map having loaded, which is the part that made earlier
  // attempts look like they did nothing: positions usually arrive before the
  // map is built, so the fit ran against a map that was absent or not yet
  // sized, marked itself done, and never ran again. Keyed by the vessel set,
  // so the page's vessel filter re-frames the map but panning is left alone.
  const fittedKey = useMemo(
    () => markers.map(p => p.transporterId).sort().join(','),
    [markers],
  );
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!isMapLoaded || !map || markers.length === 0 || fittedKeyRef.current === fittedKey)
      return;
    // Seeded with the minimum frame, so southern Norway is in view whatever
    // the fleet is doing.
    let minLng = MIN_BOUNDS.minLng;
    let minLat = MIN_BOUNDS.minLat;
    let maxLng = MIN_BOUNDS.maxLng;
    let maxLat = MIN_BOUNDS.maxLat;
    for (const p of markers) {
      minLng = Math.min(minLng, p.longitude);
      minLat = Math.min(minLat, p.latitude);
      maxLng = Math.max(maxLng, p.longitude);
      maxLat = Math.max(maxLat, p.latitude);
    }
    // Margin scaled to the map: fitBounds cannot honour padding wider than
    // the container, so a fixed figure would silently misbehave in the narrow
    // rail while looking right on the big Fleet map.
    const canvas = map.getCanvas();
    const padding = Math.max(24, Math.round(Math.min(canvas.clientWidth, canvas.clientHeight) * 0.22));
    // maxZoom is belt-and-braces now that MIN_BOUNDS gives the box a floor:
    // it caps the close-in view if that floor is ever narrowed.
    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding, maxZoom: 7, duration: 0 });
    fittedKeyRef.current = fittedKey;
  }, [isMapLoaded, markers, fittedKey]);

  function vesselAt(e: MapLayerMouseEvent): MapPosition | null {
    const index = e.features?.[0]?.properties?.['index'] as number | undefined;
    return (index != null ? markers[index] : undefined) ?? null;
  }

  function setHoveredVessel(position: MapPosition | null) {
    if (position?.transporterId !== hover?.transporterId)
      onVesselHover?.(position?.transporterId ?? null);
    setHover(position);
  }

  function handleMouseMove(e: MapLayerMouseEvent) {
    setHoveredVessel(vesselAt(e));
  }

  function handleClick(e: MapLayerMouseEvent) {
    const position = vesselAt(e);
    if (position)
      onVesselSelect?.(position.transporterId);
  }

  const isEmpty = !isLoading && allFixes.length === 0;

  return (
    <section className={twMerge('relative flex min-h-56 flex-col overflow-hidden rounded-lg border border-secondary-300 bg-primary-contrast', className)}>
      {title != null && (
        <div className="flex items-start justify-between gap-3 p-4 pb-2">
          <p className="text-sm font-semibold text-secondary-900">{title}</p>
          {note != null && <p className="text-[0.65rem] text-secondary-600">{note}</p>}
        </div>
      )}
      <div className="relative min-h-0 flex-1">
        <MapGl
          ref={mapRef}
          mapStyle={BASEMAP_STYLE_URL[theme]}
          initialViewState={INITIAL_VIEW_STATE}
          interactiveLayerIds={['vessel-points']}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredVessel(null)}
          onClick={handleClick}
          {...(onVesselSelect && hover && { cursor: 'pointer' })}
          onLoad={handleStyleReady}
          // Fires again after each basemap swap, once the new style is in.
          onStyleData={handleStyleReady}
          attributionControl={{ compact: true }}
        >
          {/* Each dash is a line of its own (see trailsGeoJson), so the layers
              have no dasharray. */}
          <Source id="vessel-trails" type="geojson" data={trailsGeoJson}>
            {theme === 'dark' && (
              <Layer
                id="vessel-trails-glow"
                type="line"
                layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                  'line-width': 10,
                  'line-blur': 6,
                  'line-color': TRAIL_GLOW_GRADIENT,
                }}
              />
            )}
            <Layer
              id="vessel-trails"
              type="line"
              layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
              paint={{
                'line-width': 4,
                'line-color': TRAIL_GRADIENT[theme],
              }}
            />
          </Source>
          <Source id="next-stops" type="geojson" data={nextStopsGeoJson}>
            <Layer
              id="next-stops"
              type="line"
              layout={{ 'line-cap': 'round' }}
              paint={{ 'line-color': CONTEXT_COLOR[theme].ink, 'line-width': 1.5, 'line-dasharray': [1, 2.5], 'line-opacity': 0.8 }}
            />
          </Source>
          <Source id="terminals" type="geojson" data={terminalsGeoJson}>
            <Layer
              id="terminal-highlight"
              type="circle"
              filter={['==', ['get', 'id'], highlightedTerminalId ?? '']}
              paint={{ 'circle-radius': 14, 'circle-color': HIGHLIGHT_COLOR, 'circle-opacity': 0.2, 'circle-stroke-color': HIGHLIGHT_COLOR, 'circle-stroke-width': 2 }}
            />
            <Layer
              id="terminal-points"
              type="circle"
              paint={{ 'circle-radius': 4, 'circle-color': CONTEXT_COLOR[theme].ink, 'circle-stroke-color': CONTEXT_COLOR[theme].halo, 'circle-stroke-width': 2 }}
            />
            <Layer
              id="terminal-labels"
              type="symbol"
              layout={{
                'text-field': ['upcase', ['get', 'name']],
                'text-font': ['Noto Sans Regular'],
                'text-size': 10,
                'text-letter-spacing': 0.05,
                // One line: a name of more than one word is not wrapped under the dot
                'text-max-width': 30,
                'text-anchor': 'left',
                'text-offset': [0.8, 0],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{ 'text-color': CONTEXT_COLOR[theme].ink, 'text-halo-color': CONTEXT_COLOR[theme].halo, 'text-halo-width': 1.5 }}
            />
          </Source>
          {readyTheme === theme && (
            <Source id="vessel-points" type="geojson" data={markersGeoJson}>
              <Layer
                id="vessel-highlight"
                type="circle"
                filter={['==', ['get', 'vesselId'], highlightedVesselId ?? '']}
                paint={{ 'circle-radius': 20, 'circle-color': HIGHLIGHT_COLOR, 'circle-opacity': 0.15, 'circle-stroke-color': HIGHLIGHT_COLOR, 'circle-stroke-width': 2 }}
              />
              {/* Pointer icon rotated by heading (falls back to course, then 0).
                The glyph aims up-left (315 degrees), so +45 aligns its tip
                north before the heading rotation. Map-aligned so it turns
                with the map. Overlap and placement are forced on across the
                marker and label layers: every vessel must stay visible, the
                per-row labelOffset handles co-located vessels. */}
              <Layer
                id="vessel-points"
                type="symbol"
                layout={{
                  'icon-image': `vessel-cursor-${theme}`,
                  'icon-rotate': ['+', ['get', 'rotation'], 45],
                  'icon-rotation-alignment': 'map',
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true,
                }}
              />
              {/* The vessel name, as its own layer. Both themes render it as
                haloed text; only the colours differ (violet on white in
                light mode, glowing near-white in dark). */}
              <Layer
                id="vessel-labels"
                type="symbol"
                layout={{
                  'text-field': ['upcase', ['get', 'name']],
                  // Must name a font the style's glyph endpoint actually serves.
                  // MapLibre's default stack is Open Sans, which OpenFreeMap
                  // does not host: every glyph range 404s and the label falls
                  // back to local rendering. Both basemaps serve Noto Sans.
                  'text-font': ['Noto Sans Bold'],
                  'text-size': 13,
                  'text-letter-spacing': 0.1,
                  'text-anchor': 'top',
                  'text-offset': ['get', 'labelOffset'],
                  'text-allow-overlap': true,
                  'text-ignore-placement': true,
                }}
                paint={{
                  'text-color': theme === 'dark' ? DARK_MARKER.labelFill : LIGHT_MARKER.arrowFill,
                  // Crisp (unblurred) halo: a blurred one smears the letters.
                  // In dark mode it is the closest MapLibre gets to the
                  // spec's layered text glow.
                  'text-halo-color': theme === 'dark' ? DARK_MARKER.labelGlow : LIGHT_MARKER.labelHalo,
                  'text-halo-width': 1.5,
                }}
              />
            </Source>
          )}
        </MapGl>
        {hover && (
          <div className="pointer-events-none absolute bottom-3 left-3 z-10 min-w-44 rounded-md border border-secondary-300 bg-primary-contrast/95 px-3 py-2 shadow-md">
            <VesselHoverCard position={hover} name={vesselNameById.get(hover.transporterId)} voyage={voyageByVessel?.get(hover.transporterId)} formatTime={formatTime} />
          </div>
        )}
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center bg-primary-contrast/70">
            <p className="px-6 text-center text-sm text-secondary-500">
              {emptyText}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// Quick summary shown in the map's bottom-left corner while a vessel marker
// is hovered: name, voyage, speed and when its position was last fetched.
function VesselHoverCard({ position, name, voyage, formatTime }: { position: MapPosition; name: string | undefined; voyage: MapVoyage | undefined; formatTime: (time: string) => string }) {
  const numFmt = useNumberFormatter({ maximumFractionDigits: 1 });
  const tonnesFmt = useNumberFormatter({ maximumFractionDigits: 0 });
  return (
    <div className="text-xs">
      <p className="font-semibold text-secondary-900">{name ?? position.transporterId}</p>
      {voyage && (
        <div className="mb-1 text-secondary-800">
          <p>{`${voyage.label} · ${voyage.status}`}</p>
          {voyage.nextStop && <p>{`Next stop ${voyage.nextStop.name} · ETA ${formatTime(voyage.nextStop.arrival)}`}</p>}
          {voyage.onBoardTonnes != null && <p>{`${tonnesFmt.format(voyage.onBoardTonnes)} t on board`}</p>}
        </div>
      )}
      <p className="text-secondary-600">
        {position.speedOverGround != null ? `${numFmt.format(position.speedOverGround)} kn` : 'Speed unknown'}
      </p>
      {/* The feed lags the vessel by ~15-25 min, so always show the fix time,
          never imply "live". */}
      <p className="mt-0.5 text-secondary-600">
        as of
        {' '}
        {formatTime(position.positionTimestamp)}
      </p>
    </div>
  );
}
